import { SpreedlyInputProperties } from '../../../services/helpers/SpreedlyIframe';
import { PaymentMethodBrand } from '../../../services/exports/Constants';
import { normalizeCardBrand } from '../../../services/helpers/cardBrand';

/**
 * What "complete" means per card brand, keyed by the `cardType` Spreedly reports in field events.
 * These rules only decide when the form may show the complete state and move the cursor on, the
 * way Stripe/Adyen do. The hard caps are Spreedly's: its number frame
 * (core.spreedly.com/iframe/number-frame-1.191.min.js, read 2026-09-15) owns BOTH hosted inputs
 * and, on every input event, sets `numberField.maxLength = getMaxLength(cardType)` (plus the
 * spacer count under prettyFormat) and `cvvField.maxLength = getCvvMaxLength(cardType)` — the
 * brand's longest CVV size, i.e. 4 for American Express and 3 for the others once the brand is
 * known, 4 while it is not. The browser enforces maxlength at insertion time, so a longer brand
 * pasted over a shorter one is clipped to the old cap (the documented caveat).
 *
 * `numberLengths` below are copied from that frame's own brand table so the form never contradicts
 * the cap: a 14-digit Discover the frame accepts must not read as incomplete here, and a 19-digit
 * JCB cannot even be typed there.
 *
 * Brands with a wide length range (Maestro 12–19, UnionPay 16–19) never auto-advance: a Luhn-valid
 * prefix is common enough there that jumping to the expiry field would interrupt a customer who
 * still has digits to type. The others advance at one fixed length — see `autoAdvanceAt`.
 *
 * Brand strings are the identifiers that frame emits: visa, master, american_express, discover,
 * diners_club, jcb, dankort, maestro, unionpay (plus Latin American and US private-label brands
 * that keep the defaults below). Cartes Bancaires is not among them — co-badged CB cards surface
 * as visa/master.
 */
interface CardBrandRule {
  numberLengths: number[];
  cvvLength: 3 | 4;
  /**
   * The one length at which the cursor moves on. Brands that issue several lengths (Visa 13/16/19,
   * Discover 14/16/19, Diners 14/16) advance at the length virtually every card in circulation
   * has, as Stripe does: a shorter form that happens to pass the checksum (one prefix in ten does)
   * must not yank a customer out of the field mid-number. Unset = never advance.
   */
  autoAdvanceAt?: number;
}

const DEFAULT_CVV_LENGTH = 3;
const UNKNOWN_BRAND_MIN_LENGTH = 16;

export const CARD_BRAND_RULES: Record<string, CardBrandRule> = {
  visa: { numberLengths: [13, 16, 18, 19], cvvLength: 3, autoAdvanceAt: 16 },
  master: { numberLengths: [16], cvvLength: 3, autoAdvanceAt: 16 },
  american_express: { numberLengths: [15], cvvLength: 4, autoAdvanceAt: 15 },
  discover: { numberLengths: [14, 16, 19], cvvLength: 3, autoAdvanceAt: 16 },
  // 14, not 16: a Diners Club International card is 14 digits. The 16-digit numbers in the frame's
  // table are the Discover/Mastercard co-brands, which the frame reports under their own brand
  // anyway — so advancing at 16 would never fire for a card that actually reports diners_club.
  diners_club: { numberLengths: [14, 16], cvvLength: 3, autoAdvanceAt: 14 },
  jcb: { numberLengths: [16], cvvLength: 3, autoAdvanceAt: 16 },
  dankort: { numberLengths: [16], cvvLength: 3, autoAdvanceAt: 16 },
  maestro: { numberLengths: [12, 13, 14, 15, 16, 17, 18, 19], cvvLength: 3 },
  unionpay: { numberLengths: [16, 17, 18, 19], cvvLength: 3 },
};

/** Spreedly `cardType` → the app's brand enum, i.e. which icon (if any) the number box shows. */
export function brandForCardType(cardType: string | null | undefined): PaymentMethodBrand | null {
  return normalizeCardBrand(cardType);
}

export function expectedCvvLength(cardType: string | null | undefined): 3 | 4 {
  return CARD_BRAND_RULES[cardType ?? '']?.cvvLength ?? DEFAULT_CVV_LENGTH;
}

/**
 * The number passes Spreedly's own check AND has a length the brand actually issues. For a brand
 * we have no rule for, fall back to "at least 16 digits", the most common length, so an unknown
 * but valid card can still reach the complete state.
 */
export function isNumberComplete(
  props: Pick<SpreedlyInputProperties, 'cardType' | 'validNumber' | 'numberLength'>,
): boolean {
  if (!props.validNumber) {
    return false;
  }

  const rule = CARD_BRAND_RULES[props.cardType ?? ''];

  if (!rule) {
    return props.numberLength >= UNKNOWN_BRAND_MIN_LENGTH;
  }

  return rule.numberLengths.includes(props.numberLength);
}

export function shouldAutoAdvanceFromNumber(
  props: Pick<SpreedlyInputProperties, 'cardType' | 'validNumber' | 'numberLength'>,
): boolean {
  const advanceAt = CARD_BRAND_RULES[props.cardType ?? '']?.autoAdvanceAt;

  return advanceAt !== undefined && props.numberLength === advanceAt && isNumberComplete(props);
}

/** Spreedly's `validCvv` is length-and-digits only; the brand decides whether 3 or 4 is complete. */
export function isCvvComplete(props: Pick<SpreedlyInputProperties, 'cardType' | 'validCvv' | 'cvvLength'>): boolean {
  return props.validCvv && props.cvvLength === expectedCvvLength(props.cardType);
}

/**
 * What to tell the customer when they leave the number field. Nothing typed → nothing to say;
 * fewer digits than the brand's longest form → still typing; a brand-length number that fails
 * Spreedly's check → a wrong digit.
 */
export function numberBlurError(
  props: Pick<SpreedlyInputProperties, 'cardType' | 'validNumber' | 'numberLength'>,
): 'number_incomplete' | 'number_invalid' | null {
  if (props.numberLength === 0 || isNumberComplete(props)) {
    return null;
  }

  const lengths = CARD_BRAND_RULES[props.cardType ?? '']?.numberLengths ?? [UNKNOWN_BRAND_MIN_LENGTH];

  if (lengths.includes(props.numberLength)) {
    return 'number_invalid';
  }

  return props.numberLength < Math.max(...lengths) ? 'number_incomplete' : 'number_invalid';
}

export function cvvBlurError(
  props: Pick<SpreedlyInputProperties, 'cardType' | 'validCvv' | 'cvvLength'>,
): 'cvv_incomplete' | 'cvv_invalid' | null {
  if (props.cvvLength === 0 || isCvvComplete(props)) {
    return null;
  }

  return props.cvvLength < expectedCvvLength(props.cardType) ? 'cvv_incomplete' : 'cvv_invalid';
}
